import { Button, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';

import { ArrowRightBlue } from '@shared/assets/icons/components';
import { ConditionalWrapper } from '@shared/ui/conditional-wrapper';
import styles from '../agent-test-chat.module.scss';

interface AgentTestRecommendationsProps {
  items: string[];
  onSelect: (value: string) => void;
  disabled?: boolean;
}

const RECOMMENDATION_LIMIT = 20;
const SCROLL_EPS = 2;

export const AgentTestRecommendations: FC<AgentTestRecommendationsProps> = ({
  items,
  onSelect,
  disabled = false,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;

    const maxScrollLeft = node.scrollWidth - node.clientWidth;
    const nextCanScrollLeft = node.scrollLeft > SCROLL_EPS;
    const nextCanScrollRight = maxScrollLeft - node.scrollLeft > SCROLL_EPS;

    setCanScrollLeft((prev) => (prev === nextCanScrollLeft ? prev : nextCanScrollLeft));
    setCanScrollRight((prev) => (prev === nextCanScrollRight ? prev : nextCanScrollRight));
  }, []);

  const scrollByStep = useCallback((direction: 1 | -1) => {
    const node = scrollerRef.current;
    if (!node) return;

    const step = Math.max(80, Math.min(140, Math.round(node.clientWidth * 0.33)));
    node.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  const handleWheel = useCallback((event: WheelEvent) => {
    const node = scrollerRef.current;
    if (!node) return;

    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    const maxScrollLeft = node.scrollWidth - node.clientWidth;
    if (maxScrollLeft <= 0) return;

    const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, node.scrollLeft + event.deltaY));
    if (nextScrollLeft === node.scrollLeft) return;

    node.scrollLeft = nextScrollLeft;
    event.preventDefault();
  }, []);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;

    updateScrollButtons();

    node.addEventListener('scroll', updateScrollButtons, { passive: true });
    node.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('resize', updateScrollButtons);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        updateScrollButtons();
      });
      observer.observe(node);
      if (node.firstElementChild instanceof HTMLElement) {
        observer.observe(node.firstElementChild);
      }
    }

    return () => {
      node.removeEventListener('scroll', updateScrollButtons);
      node.removeEventListener('wheel', handleWheel);
      window.removeEventListener('resize', updateScrollButtons);
      observer?.disconnect();
    };
  }, [items, updateScrollButtons, handleWheel]);

  if (!items.length) return null;

  return (
    <div className={styles.recommendationsRoot}>
      <div ref={scrollerRef} className={styles.recommendations}>
        <div className={styles.recommendationsList}>
          {items.map((item, index) => {
            const key = `${item}-${index}`;
            const shouldTruncate = item.length > RECOMMENDATION_LIMIT;
            const label = shouldTruncate ? `${item.slice(0, RECOMMENDATION_LIMIT)}...` : item;

            const buttonNode = (
              <Button
                key={key}
                shape="round"
                className={styles.recommendationButton}
                disabled={disabled}
                onClick={() => onSelect(item)}
              >
                <span className={styles.recommendationLabel}>{label}</span>
              </Button>
            );

            return (
              <ConditionalWrapper
                key={key}
                condition={shouldTruncate}
                wrapper={(children) => (
                  <Tooltip title={item} placement="top">
                    {children}
                  </Tooltip>
                )}
              >
                {buttonNode}
              </ConditionalWrapper>
            );
          })}
        </div>
      </div>

      {canScrollLeft && (
        <div className={`${styles.recommendationsNav} ${styles.recommendationsNavLeft}`}>
          <button
            type="button"
            className={styles.recommendationsNavButton}
            onClick={() => scrollByStep(-1)}
            aria-label="scroll recommendations left"
          >
            <ArrowRightBlue className={styles.recommendationsNavArrowLeft} />
          </button>
        </div>
      )}

      {canScrollRight && (
        <div className={`${styles.recommendationsNav} ${styles.recommendationsNavRight}`}>
          <button
            type="button"
            className={styles.recommendationsNavButton}
            onClick={() => scrollByStep(1)}
            aria-label="scroll recommendations right"
          >
            <ArrowRightBlue />
          </button>
        </div>
      )}
    </div>
  );
};
